import type { CalendarEvent, CalendarEvents, CalendarPersona, CalendarSummary, TaskBlock } from "../api/generated";

/** The id of the extra lane for planned tasks that have no persona. */
export const UNASSIGNED_LANE = "unassigned";

/** One column of the day: a persona, its calendars, its events and the time planned for its tasks. */
export type Lane = {
  persona: CalendarPersona;
  calendars: CalendarSummary[];
  allDay: CalendarEvent[];
  timed: CalendarEvent[];
  blocks: TaskBlock[];
};

/**
 * A lane per persona, with the events of its calendars (from `shown`, which leaves out hidden
 * calendars) and the blocks planned for its tasks. Blocks for tasks with no persona get an extra
 * lane at the end, only when there are any. Blocks for personas not shown are left out.
 */
export function dayLanes(data: CalendarEvents, shown: CalendarEvent[], blocks: TaskBlock[]): Lane[] {
  const calendars = new Map(data.calendars.map((calendar) => [calendar.id, calendar]));
  const lanes = data.personas.map((persona) => {
    const events = shown.filter((event) => calendars.get(event.calendarId)?.personaIds.includes(persona.id));
    return {
      persona,
      calendars: data.calendars.filter((calendar) => calendar.personaIds.includes(persona.id)),
      allDay: events.filter((event) => event.allDay),
      timed: events.filter((event) => !event.allDay),
      blocks: blocks.filter((block) => block.personaId === persona.id),
    };
  });
  const unassigned = blocks.filter((block) => block.personaId === null);
  if (unassigned.length === 0) return lanes;
  return [...lanes, { persona: { id: UNASSIGNED_LANE, name: "No persona" }, calendars: [], allDay: [], timed: [], blocks: unassigned }];
}
